import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SelectField from "../components/common/SelectField";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";

const countryCodeOptions = [
  { value: "+1", label: "+1" },
  { value: "+91", label: "+91" },
];

const POSITIONS = ["Vice President", "Secretary", "Treasurer", "Social Secretary"];

export default function Nomination() {
  const navigate = useNavigate();

  const [loadingSetting, setLoadingSetting] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [setting, setSetting] = useState(null);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);

  // Address lookup options
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [formData, setFormData] = useState({
    full_name: "",
    country_code: "+1",
    phone: "",
    email: "",
    address: {
      address_text: "",
      country: "",
      state: "",
      city: "",
      zip: "",
    },
    position: "",
    past_service: {
      vice_president_years: "",
      secretary_years: "",
      treasurer_years: "",
      social_secretary_years: "",
      india_festival_chair_years: "",
    },
    cert1: false,
    cert2: false,
    signature_name: "",
  });

  const [errors, setErrors] = useState({});

  const fetchSetting = async () => {
    try {
      setLoadingSetting(true);
      const res = await API.get(api_paths.member.NOMINATION.GET_SETTING);
      const data = res.data?.payload?.result || null;
      if (!data?.isOpen) {
        navigate("/", { replace: true });
        return;
      }
      setSetting(data);
    } catch (err) {
      console.error("Error fetching nomination setting:", err);
      navigate("/", { replace: true });
    } finally {
      setLoadingSetting(false);
    }
  };

  const getCountry = async () => {
    try {
      const response = await API.get(
        `${api_paths.address.GET_COUNTRY}?pagination=false`
      );
      if (response.data?.payload?.data) {
        const list = response.data.payload.data;
        setCountries(list);

        if (list.length > 0) {
          const usCountry =
            list.find(
              (c) =>
                c.name?.toLowerCase() === "united states" ||
                c.name?.toLowerCase() === "usa"
            ) || list[0];

          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              country: usCountry._id,
              state: "",
              city: "",
            },
          }));

          getState(usCountry._id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch country:", error);
    }
  };

  const getState = async (countryId) => {
    try {
      const response = await API.get(
        `${api_paths.address.GET_STATE}?country_id=${countryId}&pagination=false`
      );

      if (response.data?.payload?.data) {
        const list = response.data.payload.data;
        setStates(list);
        setCities([]);

        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            state: "",
            city: "",
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch state:", error);
    }
  };

  const getCity = async (stateId) => {
    try {
      const response = await API.get(
        `${api_paths.address.GET_CITY}?state_id=${stateId}&pagination=false`
      );

      if (response.data?.payload?.data) {
        setCities(response.data.payload.data);
        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            city: "",
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch city:", error);
    }
  };

  useEffect(() => {
    fetchSetting();
    getCountry();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("past_service.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        past_service: {
          ...prev.past_service,
          [field]: value,
        },
      }));
      if (errors.past_service) {
        setErrors((prev) => ({ ...prev, past_service: null }));
      }
      if (errors[`past_service.${field}`]) {
        setErrors((prev) => ({ ...prev, [`past_service.${field}`]: null }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const isValidYearInput = (str) => {
    if (!str || !str.trim()) return true;
    const parts = str.split(/[,/\-\s]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return false;

    const currentYear = new Date().getFullYear();
    for (const part of parts) {
      if (!/^\d{4}$/.test(part)) return false;
      const yr = parseInt(part, 10);
      if (yr < 1970 || yr > currentYear + 2) return false;
    }
    return true;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) newErrors.full_name = "Full Name is required";
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      formData.country_code === "+1" &&
      formData.phone.replace(/\D/g, "").length < 10
    ) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.address.address_text?.trim()) {
      newErrors["address.address_text"] = "Street Address is required";
    }
    if (!formData.address.country) {
      newErrors["address.country"] = "Country is required";
    }
    if (!formData.address.state) {
      newErrors["address.state"] = "State is required";
    }
    if (!formData.address.city) {
      newErrors["address.city"] = "City is required";
    }
    if (!formData.address.zip?.trim()) {
      newErrors["address.zip"] = "ZIP code is required";
    } else if (!/^\d{5}$/.test(formData.address.zip.trim())) {
      newErrors["address.zip"] = "ZIP code must be 5 digits";
    }

    if (!formData.position) {
      newErrors.position = "Please select a position you are nominating yourself for.";
    }

    if (formData.position === "Vice President") {
      const { secretary_years, treasurer_years, social_secretary_years, india_festival_chair_years } =
        formData.past_service;
      if (
        !secretary_years?.trim() &&
        !treasurer_years?.trim() &&
        !social_secretary_years?.trim() &&
        !india_festival_chair_years?.trim()
      ) {
        newErrors.past_service =
          "Vice President candidates must specify prior year(s) served in at least one eligible position (Secretary, Treasurer, Social Secretary, or India Festival Chair).";
      }

      const yearFields = [
        { key: "secretary_years", label: "Secretary" },
        { key: "treasurer_years", label: "Treasurer" },
        { key: "social_secretary_years", label: "Social Secretary" },
        { key: "india_festival_chair_years", label: "India Festival Chair" },
      ];

      yearFields.forEach(({ key, label }) => {
        const val = formData.past_service[key];
        if (val && val.trim() && !isValidYearInput(val)) {
          newErrors[`past_service.${key}`] = `Please enter valid 4-digit year(s) between 1970 and 2028 for ${label}.`;
        }
      });
    }

    if (!formData.cert1 || !formData.cert2) {
      newErrors.certification = "You must confirm this declaration to proceed.";
    }

    if (!formData.signature_name.trim()) {
      newErrors.signature_name = "Electronic Signature is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData = {
      full_name: formData.full_name,
      phone: `${formData.country_code} ${formData.phone}`,
      email: formData.email,
      address: formData.address,
      position: formData.position,
      past_service: formData.position === "Vice President" ? formData.past_service : {},
      declaration_accepted: Boolean(formData.cert1 && formData.cert2),
      signature_name: formData.signature_name,
      signature_date: new Date(),
    };

    try {
      setSubmitting(true);
      await API.post(api_paths.member.NOMINATION.SUBMIT, submitData);
      setSubmittedSuccess(true);
    } catch (err) {
      console.error("Submission error:", err);
      const msg = err.response?.data?.message || "Failed to submit nomination. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const getTodayFormatted = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatOpenDate = (dateStr) => {
    if (!dateStr) return "Thursday, October 1, 2026";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "America/New_York",
    });
  };

  const formatCloseDate = (dateStr) => {
    if (!dateStr) return "Saturday, October 10, 2026 · 5:00 PM";
    const d = new Date(dateStr);
    const dateFormatted = d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "America/New_York",
    });
    const timeFormatted = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    });
    return `${dateFormatted} · ${timeFormatted}`;
  };

  const getBannerText = (s) => {
    if (!s?.start_date_time) return "Nominations open Thursday, October 1, 2026.";
    const now = new Date();
    const start = new Date(s.start_date_time);
    const end = new Date(s.end_date_time);

     return `Nominations open ${formatOpenDate(s.start_date_time)}.`;
    // if (now < start) {
    //   return `Nominations open ${formatOpenDate(s.start_date_time)}.`;
    // } else if (now >= start && now <= end) {
    //   return `Nominations are currently open until ${formatCloseDate(s.end_date_time)}.`;
    // } else {
    //   return `Nominations closed on ${formatCloseDate(s.end_date_time)}.`;
    // }
  };

  if (loadingSetting || !setting?.isOpen) {
    return (
      <Container className="my-5">
        <Skeleton height={50} className="mb-3" />
        <Skeleton height={300} />
      </Container>
    );
  }

  return (
    <div className="registration-page nomination-page py-4">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            {/* Header section */}
            <div className="text-center mb-4">
              <div
                className="d-inline-block px-3 py-1 mb-2 rounded-pill fw-bold text-white shadow-sm"
                style={{ backgroundColor: "#d97706", fontSize: "12px", letterSpacing: "1px" }}
              >
                ELECTIONS {setting?.election_year || "2027"}
              </div>
              <h2 className="fw-bold mb-1" style={{ color: "#8b1818" }}>
                {setting?.title || "2027 Executive Committee Nomination Form"}
              </h2>
              <p className="text-muted fs-6 mb-4">Gujarati Samaj of Tampa Bay Inc.</p>

              {/* 3 Info Cards */}
              <Row className="g-3 justify-content-center text-start mb-3">
                <Col md={4}>
                  <div
                    className="p-3 bg-white rounded-3 shadow-sm h-100"
                    style={{ border: "1px solid #e5e7eb", borderLeft: "4px solid #8b1818" }}
                  >
                    <div
                      className="text-muted small fw-semibold text-uppercase"
                      style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                    >
                      ELECTION DATE
                    </div>
                    <div className="fw-bold text-dark mt-1" style={{ fontSize: "15px" }}>
                      {setting?.election_date_text || "Sunday, November 15, 2026"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div
                    className="p-3 bg-white rounded-3 shadow-sm h-100"
                    style={{ border: "1px solid #e5e7eb", borderLeft: "4px solid #8b1818" }}
                  >
                    <div
                      className="text-muted small fw-semibold text-uppercase"
                      style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                    >
                      NOMINATIONS OPEN
                    </div>
                    <div className="fw-bold text-dark mt-1" style={{ fontSize: "15px" }}>
                      {formatOpenDate(setting?.start_date_time)}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div
                    className="p-3 bg-white rounded-3 shadow-sm h-100"
                    style={{ border: "1px solid #e5e7eb", borderLeft: "4px solid #8b1818" }}
                  >
                    <div
                      className="text-muted small fw-semibold text-uppercase"
                      style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                    >
                      NOMINATIONS CLOSE
                    </div>
                    <div className="fw-bold text-dark mt-1" style={{ fontSize: "15px" }}>
                      {formatCloseDate(setting?.end_date_time)}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Status Alert Banner */}
              <div
                className="p-2 px-3 rounded-2 text-center mb-4"
                style={{
                  backgroundColor: "#fff9db",
                  border: "1px solid #ffecb5",
                  color: "#7a5200",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                {getBannerText(setting)}
              </div>
            </div>

            {/* Submission Success Screen */}
            {submittedSuccess ? (
              <div className="register-card p-5 text-center position-relative">
                <div className="decor-glow" aria-hidden />
                <div style={{ fontSize: "56px", color: "#2e7d32" }} className="mb-3">
                  ✓
                </div>
                <h3 className="fw-bold text-success mb-3">Nomination Submitted Successfully!</h3>
                <div>
                  <button
                    className="btn btn-orange px-4 py-2 fw-semibold"
                    onClick={() => navigate("/")}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            ) : (
              /* Main Nomination Form Card with register-card styling */
              <div className="register-card p-4 p-md-5 position-relative shadow-sm">
                <div className="decor-glow" aria-hidden />
                <Form onSubmit={handleSubmit}>
                  {/* 1. Candidate Details */}
                  <h4
                    className="fw-bold mb-3"
                    style={{
                      color: "#8b1818",
                      borderBottom: "1px solid #e5e7eb",
                      paddingBottom: "8px",
                      fontSize: "18px",
                    }}
                  >
                    1. Candidate Details
                  </h4>

                  <Row className="g-3 mb-4">
                    {/* Full Name */}
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Full Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          isInvalid={!!errors.full_name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.full_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Phone Number */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Phone Number <span className="text-danger">*</span>
                        </Form.Label>
                        <Row className="g-2">
                          <Col xs={4} md={4}>
                            <SelectField
                              value={countryCodeOptions.find(
                                (opt) => opt.value === formData.country_code
                              )}
                              options={countryCodeOptions}
                              onChange={(opt) => {
                                const newCode = opt.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  country_code: newCode,
                                  phone:
                                    newCode === "+1"
                                      ? formatUSPhone(normalizePhoneForSave(prev.phone))
                                      : prev.phone.replace(/\D/g, ""),
                                }));
                                if (errors.phone)
                                  setErrors((prev) => ({ ...prev, phone: null }));
                              }}
                            />
                          </Col>
                          <Col xs={8} md={8}>
                            <Form.Control
                              type="tel"
                              name="phone"
                              placeholder={
                                formData.country_code === "+1"
                                  ? "(813) 555-0123"
                                  : "Enter phone number"
                              }
                              value={formData.phone}
                              onChange={(e) => {
                                let value = e.target.value;
                                if (formData.country_code === "+1") {
                                  const digits = value.replace(/\D/g, "").slice(0, 10);
                                  value = formatUSPhone(digits);
                                } else {
                                  value = value.replace(/\D/g, "").slice(0, 15);
                                }
                                setFormData((prev) => ({ ...prev, phone: value }));
                                if (errors.phone)
                                  setErrors((prev) => ({ ...prev, phone: null }));
                              }}
                              maxLength={formData.country_code === "+1" ? 14 : 15}
                              isInvalid={!!errors.phone}
                            />
                          </Col>
                        </Row>
                        {errors.phone && (
                          <div className="invalid-feedback d-block mt-1">
                            {errors.phone}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Email */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="yourname@example.com"
                          isInvalid={!!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Street Address */}
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Street Address <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address.address_text"
                          value={formData.address.address_text}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              address: { ...prev.address, address_text: e.target.value },
                            }));
                            if (errors["address.address_text"])
                              setErrors((prev) => ({
                                ...prev,
                                "address.address_text": null,
                              }));
                          }}
                          placeholder="Enter street address"
                          isInvalid={!!errors["address.address_text"]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors["address.address_text"]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Country */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Country <span className="text-danger">*</span>
                        </Form.Label>
                        <SelectField
                          value={
                            countries
                              .map((c) => ({ value: c._id, label: c.name }))
                              .find((o) => o.value === formData.address.country) || null
                          }
                          options={countries.map((c) => ({
                            value: c._id,
                            label: c.name,
                          }))}
                          onChange={(opt) => {
                            setFormData((prev) => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                country: opt.value,
                                state: "",
                                city: "",
                              },
                            }));
                            if (errors["address.country"])
                              setErrors((prev) => ({
                                ...prev,
                                "address.country": null,
                              }));
                            getState(opt.value);
                          }}
                          error={errors["address.country"]}
                        />
                      </Form.Group>
                    </Col>

                    {/* State */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          State <span className="text-danger">*</span>
                        </Form.Label>
                        <SelectField
                          value={
                            formData.address.state
                              ? states
                                .map((s) => ({ value: s._id, label: s.name }))
                                .find((o) => o.value === formData.address.state) || null
                              : null
                          }
                          options={states.map((s) => ({
                            value: s._id,
                            label: s.name,
                          }))}
                          onChange={(opt) => {
                            setFormData((prev) => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                state: opt.value,
                                city: "",
                              },
                            }));
                            if (errors["address.state"])
                              setErrors((prev) => ({
                                ...prev,
                                "address.state": null,
                              }));
                            getCity(opt.value);
                          }}
                          error={errors["address.state"]}
                        />
                      </Form.Group>
                    </Col>

                    {/* City */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          City <span className="text-danger">*</span>
                        </Form.Label>
                        <SelectField
                          value={
                            formData.address.city
                              ? cities
                                .map((c) => ({ value: c._id, label: c.name }))
                                .find((o) => o.value === formData.address.city) || null
                              : null
                          }
                          options={cities.map((c) => ({
                            value: c._id,
                            label: c.name,
                          }))}
                          onChange={(opt) => {
                            setFormData((prev) => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                city: opt.value,
                              },
                            }));
                            if (errors["address.city"])
                              setErrors((prev) => ({
                                ...prev,
                                "address.city": null,
                              }));
                          }}
                          error={errors["address.city"]}
                        />
                      </Form.Group>
                    </Col>

                    {/* ZIP */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          ZIP <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address.zip"
                          value={formData.address.zip}
                          maxLength={5}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "").slice(0, 5);
                            setFormData((prev) => ({
                              ...prev,
                              address: { ...prev.address, zip: val },
                            }));
                            if (errors["address.zip"])
                              setErrors((prev) => ({
                                ...prev,
                                "address.zip": null,
                              }));
                          }}
                          placeholder="Enter 5-digit ZIP code"
                          isInvalid={!!errors["address.zip"]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors["address.zip"]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* 2. Position (select only one) */}
                  <h4
                    className="fw-bold mb-2 mt-4"
                    style={{
                      color: "#8b1818",
                      borderBottom: "1px solid #e5e7eb",
                      paddingBottom: "8px",
                      fontSize: "18px",
                    }}
                  >
                    2. Position (select only one) <span className="text-danger">*</span>
                  </h4>
                  <p className="text-muted small mb-3">
                    I nominate myself for the {setting?.election_year || "2027"} Executive Committee for the following position:
                  </p>

                  <Row className="g-3 mb-4">
                    {POSITIONS.map((pos) => (
                      <Col sm={6} md={3} key={pos}>
                        <div
                          className={`p-3 rounded-3 border d-flex align-items-center h-100 ${formData.position === pos
                            ? "border-danger bg-light shadow-sm"
                            : "bg-white"
                            }`}
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, position: pos }));
                            if (errors.position)
                              setErrors((prev) => ({ ...prev, position: null }));
                          }}
                          style={{
                            cursor: "pointer",
                            borderColor: formData.position === pos ? "#8b1818" : "#dee2e6",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Form.Check
                            type="radio"
                            id={`pos-${pos}`}
                            name="position"
                            label={
                              <span
                                className="fw-semibold ms-2 text-dark"
                                style={{ fontSize: "14px" }}
                              >
                                {pos}
                              </span>
                            }
                            checked={formData.position === pos}
                            onChange={() => {
                              setFormData((prev) => ({ ...prev, position: pos }));
                              if (errors.position)
                                setErrors((prev) => ({ ...prev, position: null }));
                            }}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>

                  {errors.position && (
                    <div className="text-danger small mb-3" style={{ marginTop: "-10px" }}>
                      {errors.position}
                    </div>
                  )}

                  {/* Prior Service for Vice President */}
                  {formData.position === "Vice President" && (
                    <div className="p-4 mb-4 rounded-3 border bg-light">
                      <p className="fw-bold mb-2 text-dark small">
                        Prior Service (Vice President Eligibility): Please specify the year(s) when you served in eligible position(s):
                      </p>
                      {errors.past_service && (
                        <div className="alert alert-warning py-2 mb-3 small">
                          {errors.past_service}
                        </div>
                      )}
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold small">Secretary</Form.Label>
                            <Form.Control
                              type="text"
                              name="past_service.secretary_years"
                              value={formData.past_service.secretary_years}
                              onChange={handleChange}
                              placeholder="e.g. 2024, 2025"
                              isInvalid={!!errors["past_service.secretary_years"]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors["past_service.secretary_years"]}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold small">Treasurer</Form.Label>
                            <Form.Control
                              type="text"
                              name="past_service.treasurer_years"
                              value={formData.past_service.treasurer_years}
                              onChange={handleChange}
                              placeholder="e.g. 2023"
                              isInvalid={!!errors["past_service.treasurer_years"]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors["past_service.treasurer_years"]}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold small">Social Secretary</Form.Label>
                            <Form.Control
                              type="text"
                              name="past_service.social_secretary_years"
                              value={formData.past_service.social_secretary_years}
                              onChange={handleChange}
                              placeholder="e.g. 2022"
                              isInvalid={!!errors["past_service.social_secretary_years"]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors["past_service.social_secretary_years"]}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold small">India Festival Chair</Form.Label>
                            <Form.Control
                              type="text"
                              name="past_service.india_festival_chair_years"
                              value={formData.past_service.india_festival_chair_years}
                              onChange={handleChange}
                              placeholder="e.g. 2021"
                              isInvalid={!!errors["past_service.india_festival_chair_years"]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors["past_service.india_festival_chair_years"]}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {/* 3. Certification */}
                  <h4
                    className="fw-bold mb-3 mt-4"
                    style={{
                      color: "#8b1818",
                      borderBottom: "1px solid #e5e7eb",
                      paddingBottom: "8px",
                      fontSize: "18px",
                    }}
                  >
                    3. Certification
                  </h4>

                  <div
                    className="p-3 mb-4 rounded-3"
                    style={{ backgroundColor: "#fffdf5", border: "1px solid #f3e8c8" }}
                  >
                    <div className="form-check d-flex align-items-start mb-3">
                      <input
                        className="form-check-input flex-shrink-0 mt-1"
                        type="checkbox"
                        id="cert1"
                        name="cert1"
                        checked={formData.cert1}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, cert1: e.target.checked }));
                          if (errors.certification) setErrors((prev) => ({ ...prev, certification: null }));
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        htmlFor="cert1"
                        className="form-check-label small text-dark mb-0"
                        style={{ lineHeight: "1.5", cursor: "pointer" }}
                      >
                        I confirm that I am nominating myself for <strong>only one position</strong> for the {setting?.election_year || "2027"} term. I understand that submitting nominations for more than one position will result in automatic cancellation of all nominations. <span className="text-danger">*</span>
                      </label>
                    </div>

                    <div className="form-check d-flex align-items-start mb-0">
                      <input
                        className="form-check-input flex-shrink-0 mt-1"
                        type="checkbox"
                        id="cert2"
                        name="cert2"
                        checked={formData.cert2}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, cert2: e.target.checked }));
                          if (errors.certification) setErrors((prev) => ({ ...prev, certification: null }));
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        htmlFor="cert2"
                        className="form-check-label small text-dark mb-0"
                        style={{ lineHeight: "1.5", cursor: "pointer" }}
                      >
                        I have reviewed the GSTB By-laws and confirm that I meet the eligibility requirements for the position I am seeking. For Vice President, I understand that the candidate must have previously served at least one year as Secretary, Treasurer, Social Secretary, or as India Festival Chair, as required by the By-laws. Secretary, Treasurer, and Social Secretary positions are open to any adult Member in good standing. <span className="text-danger">*</span>
                      </label>
                    </div>

                    {errors.certification && (
                      <div className="text-danger small mt-3 ps-1">{errors.certification}</div>
                    )}
                  </div>

                  {/* Electronic Signature and Date */}
                  <Row className="g-3 mb-4">
                    <Col md={7}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Electronic Signature <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="signature_name"
                          value={formData.signature_name}
                          onChange={handleChange}
                          placeholder="Type your full name"
                          isInvalid={!!errors.signature_name}
                        />
                        <div className="text-muted small mt-1">
                          Typing your name here acts as your signature.
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {errors.signature_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={5}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Date</Form.Label>
                        <Form.Control
                          type="text"
                          readOnly
                          value={getTodayFormatted()}
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Submit Button */}
                  <div className="mt-4 text-center">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="btn btn-orange w-100 py-2 fw-semibold"
                      style={{
                        fontSize: "16px",
                      }}
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Nomination"
                      )}
                    </Button>
                    
                  </div>
                </Form>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
